import React, { useState, useEffect, useRef, useCallback } from "react";
import NovoEventoService from "../../services/Eventos/NovoEventoService";
import InscricaoService from "../../services/Inscricoes/InscricaoService";
import { Button, Input, Toast, Spinner } from "../ui";
import { Check, RefreshCw, Loader2, Ticket } from "lucide-react";

function Voucher({ corridaId, onClose }) {
    const [voucher, setVoucher] = useState("");
    const [qtdVoucher, setQtdVoucher] = useState("");
    const [submitted, setSubmitted] = useState(false);
    const [saving, setSaving] = useState(false);
    const [serverError, setServerError] = useState("");
    const [vouchersList, setVouchersList] = useState([]);
    const [loadingVouchers, setLoadingVouchers] = useState(false);
    const toast = useRef(null);

    const eventoServiceRef = useRef(new NovoEventoService());
    const inscricaoServiceRef = useRef(new InscricaoService());

    const extractVouchersList = (data) => {
        if (!data) return [];
        if (Array.isArray(data)) return data;
        if (Array.isArray(data.results)) return data.results;
        if (Array.isArray(data.vouchers)) return data.vouchers;
        if (Array.isArray(data.data)) return data.data;
        if (typeof data === 'object') {
            const values = Object.values(data);
            const arrayVal = values.find(v => Array.isArray(v));
            if (arrayVal) return arrayVal;
        }
        return [];
    };

    const carregarVouchers = useCallback(async () => {
        if (!corridaId) return;
        setLoadingVouchers(true);
        try {
            const response = await inscricaoServiceRef.current.getVouchersPerCorrida(corridaId);
            if (response.ok) {
                const data = await response.json();
                setVouchersList(extractVouchersList(data));
            } else {
                console.warn("Resposta não-ok ao carregar vouchers:", response.status);
            }
        } catch (error) {
            console.error("Erro ao carregar vouchers:", error);
        } finally {
            setLoadingVouchers(false);
        }
    }, [corridaId]);

    useEffect(() => {
        carregarVouchers();
    }, [carregarVouchers]);

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        setSubmitted(true);
        setServerError("");

        const trimmedVoucher = voucher.trim();
        const numQtd = parseInt(qtdVoucher, 10);
        if (!trimmedVoucher || isNaN(numQtd) || numQtd <= 0 || !corridaId) {
            return;
        }

        const data = {
            nome_voucher: trimmedVoucher,
            voucher: trimmedVoucher,
            codigo_voucher: trimmedVoucher,
            qtdVouchers: numQtd,
            qtd_vouchers: numQtd,
            quantidade: numQtd,
            id_corrida: corridaId,
            corrida: corridaId
        };

        try {
            setSaving(true);
            const response = await eventoServiceRef.current.setNovoVoucher(JSON.stringify(data));
            if (!response.ok && response.status !== 201 && response.status !== 200) {
                let errorMsg = `Erro ao criar voucher: ${response.status}`;
                try {
                    const errData = await response.json();
                    if (errData.erro) errorMsg = errData.erro;
                    else if (errData.detail) errorMsg = errData.detail;
                    else if (errData.mensagem) errorMsg = errData.mensagem;
                    else if (errData.nome_voucher) errorMsg = Array.isArray(errData.nome_voucher) ? errData.nome_voucher.join(" ") : errData.nome_voucher;
                } catch (_) {}
                throw new Error(errorMsg);
            }

            toast.current?.show({
                severity: 'success',
                summary: 'Sucesso!',
                detail: 'Voucher criado com sucesso!',
                life: 3000
            });

            setVoucher("");
            setQtdVoucher("");
            setSubmitted(false);
            await carregarVouchers();
        } catch (error) {
            console.error("Erro ao criar voucher:", error);
            setServerError(error.message || "Não foi possível criar o voucher.");
            toast.current?.show({
                severity: 'error',
                summary: 'Erro!',
                detail: error.message || "Não foi possível criar o voucher.",
                life: 4000
            });
        } finally {
            setSaving(false);
        }
    };

    const voucherNameBody = (rowData) => {
        return rowData.nome_voucher || rowData.voucher || rowData.codigo || rowData.nome || rowData.codigo_voucher || "-";
    };

    const qtdBody = (rowData) => {
        const val = rowData.qtdVouchers ?? rowData.qtd_vouchers ?? rowData.quantidade ?? rowData.qtd ?? rowData.quantidade_total;
        return val !== undefined && val !== null ? val : "-";
    };

    const saldoBody = (rowData) => {
        const val = rowData.qtdDisponivel ?? rowData.saldo ?? rowData.saldo_uso ?? rowData.saldo_de_uso ?? rowData.qtd_disponivel;
        if (val !== undefined && val !== null) return val;
        const total = rowData.qtdVouchers ?? rowData.qtd_vouchers ?? rowData.quantidade ?? rowData.qtd ?? rowData.quantidade_total;
        const usados = rowData.qtdUsados ?? rowData.usados ?? rowData.qtd_usada ?? rowData.quantidade_usada ?? rowData.qtd_utilizada ?? 0;
        if (total !== undefined && total !== null) {
            const numTotal = Number(total);
            const numUsados = Number(usados || 0);
            if (!Number.isNaN(numTotal) && !Number.isNaN(numUsados)) {
                return numTotal - numUsados;
            }
        }
        return "-";
    };

    return (
        <div className="flex flex-col gap-5 text-white">
            <Toast ref={toast} />

            <form onSubmit={handleSubmit} className="p-4 rounded-xl bg-slate-900 border border-urb-border flex flex-col gap-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className="text-xs font-semibold text-slate-300 mb-1.5 block">
                            Código / Nome do Voucher <span className="text-red-400">*</span>
                        </label>
                        <Input
                            placeholder="Ex: PARCEIRO10, CORTESIAVIP"
                            value={voucher}
                            onChange={(e) => setVoucher(e.target.value)}
                            error={submitted && !voucher.trim() ? "Voucher é obrigatório" : null}
                            disabled={saving}
                        />
                    </div>

                    <div>
                        <label className="text-xs font-semibold text-slate-300 mb-1.5 block">
                            Quantidade Disponível <span className="text-red-400">*</span>
                        </label>
                        <Input
                            type="number"
                            min="1"
                            placeholder="Ex: 5"
                            value={qtdVoucher}
                            onChange={(e) => setQtdVoucher(e.target.value)}
                            error={submitted && (!qtdVoucher || parseInt(qtdVoucher, 10) <= 0) ? "Quantidade inválida" : null}
                            disabled={saving}
                        />
                    </div>
                </div>

                {serverError && (
                    <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-xs">
                        {serverError}
                    </div>
                )}

                <div className="flex justify-end">
                    <Button
                        type="submit"
                        disabled={saving}
                        className="px-6 py-2.5 font-bold text-xs shadow-neon flex items-center gap-2"
                    >
                        {saving ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Salvando...
                            </>
                        ) : (
                            <>
                                <Check className="w-4 h-4" />
                                Criar Voucher
                            </>
                        )}
                    </Button>
                </div>
            </form>

            <div>
                <div className="flex items-center justify-between mb-3">
                    <h4 className="font-bold text-sm text-slate-300 m-0 flex items-center gap-2">
                        <Ticket className="w-4 h-4 text-urb-primary" />
                        Vouchers Criados ({vouchersList.length})
                    </h4>
                    <button
                        type="button"
                        onClick={carregarVouchers}
                        disabled={loadingVouchers}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                        title="Atualizar lista"
                    >
                        <RefreshCw className={`w-4 h-4 ${loadingVouchers ? 'animate-spin' : ''}`} />
                    </button>
                </div>

                <div className="border border-urb-border rounded-xl overflow-hidden shadow-md">
                    {loadingVouchers && vouchersList.length === 0 ? (
                        <div className="flex justify-center py-8">
                            <Spinner size="md" />
                        </div>
                    ) : vouchersList.length === 0 ? (
                        <div className="p-6 text-center text-slate-400 text-xs">
                            Nenhum voucher encontrado para esta corrida.
                        </div>
                    ) : (
                        <table className="w-full text-left text-xs">
                            <thead className="bg-slate-900 text-slate-400 uppercase font-semibold text-[11px] border-b border-urb-border">
                                <tr>
                                    <th className="p-3">Código do Voucher</th>
                                    <th className="p-3">Quantidade Total</th>
                                    <th className="p-3">Saldo Disponível</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-urb-border bg-urb-card">
                                {vouchersList.map((row, idx) => (
                                    <tr key={idx} className="hover:bg-slate-900/30 transition-colors">
                                        <td className="p-3 font-mono font-bold text-urb-primary">
                                            {voucherNameBody(row)}
                                        </td>
                                        <td className="p-3 text-white font-semibold">
                                            {qtdBody(row)}
                                        </td>
                                        <td className="p-3 font-semibold text-slate-300">
                                            {saldoBody(row)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}

export default Voucher;
